import React, { useCallback, useContext, useMemo, useRef } from 'react';
import {
  Flex,
  Carousel,
  RemoteImage,
  useScreenSize,
  IconFullscreen,
  IconArrowLeft,
  IconArrowRight,
  ColorValues,
  type SliderRef,
  Colors,
} from '@banhala/ui-kit';
import Animated, {
  interpolate,
  type SharedValue,
  scrollTo as reanimatedScrollTo,
  useAnimatedRef,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { type NativeScrollEvent } from 'react-native';
import { ScrollContext } from '../../context/ScrollContext';
import { RelatedGoodsView } from './components/RelatedGoodsView';
import { BlurBackground } from './components/BlurBackground';
import { isIOS, isNotNil, isWeb } from 'shared/src/utils';
import { useGoodsBasic } from '../../queries/useGoodsBasic';
import { usePreRenderParams } from '../../hooks/usePreRenderParams';
import { DetailedImageViewer } from './components/DetailedImageViewer';
import { AiStudioRedirect } from './components/AiStudioRedirect';
import { PaginationDots } from './components/PaginationDots';
import { useSimilarGoods } from '../../queries/useSimilarGoods';
import { ITEM_SHOW_COUNT } from './components/RelatedGoodsView';
import { ScrollView } from 'react-native-gesture-handler';

const AnimatedScrollView = Animated.createAnimatedComponent(ScrollView);

// 현재 페이지 기준 앞뒤 몇 장까지 실제 이미지를 마운트할지.
// 창 밖 페이지는 width 칸(placeholder)만 유지해 paging offset은 보존하되,
// RemoteImage(→ NetworkImageView → SDWebImage 디코드)는 마운트하지 않아
// 풀 해상도 비트맵이 메모리에 쌓이는 것을 막는다.
const RENDER_WINDOW = 1;

type CarouselItem = { type: 'image'; uri: string } | { type: 'related' };

const CoverImageItem = React.memo(
  ({
    uri,
    width,
    index,
    currentIndexValue,
    scrollY,
    onPress,
  }: {
    uri: string;
    width: number;
    index: number;
    currentIndexValue: SharedValue<number>;
    scrollY?: SharedValue<number>;
    onPress: () => void;
  }) => {
    const style = useAnimatedStyle(() => {
      if (currentIndexValue.value !== index || !scrollY || scrollY.value > 0) {
        return { overflow: 'hidden' };
      }
      return {
        overflow: 'hidden',
        transform: [
          {
            scale: interpolate(
              scrollY.value,
              [-width * 0.5, 0],
              [2, 1],
              'clamp',
            ),
          },
        ],
      };
    });

    return (
      <Animated.View style={style}>
        <Flex
          width={width}
          height={width}
          testID={`상품대표이미지-${index}`}
          onPress={onPress}
        >
          <RemoteImage uri={uri} width={width} height={width} />
        </Flex>
      </Animated.View>
    );
  },
);

export const CoverImages: React.FC = () => {
  const { width } = useScreenSize();

  // 웹: reanimated-carousel 사용 (scroll-snap 다중 페이지 넘김 이슈)
  // 네이티브: Animated.ScrollView + pagingEnabled 사용 (가볍고 성능 좋음)
  const sliderRef = useRef<SliderRef | null>(null);
  const scrollViewRef = useAnimatedRef<Animated.ScrollView>();

  const { scrollY } = useContext(ScrollContext);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const currentIndexValue = useSharedValue(0);
  const { data: goodsBasic, isSuccess: successBasicData } = useGoodsBasic();
  const { data: goodsBasicSkeleton } = usePreRenderParams();
  const { data: similarGoods } = useSimilarGoods(true);
  const hasSimilarGoods = (similarGoods?.goods?.length ?? 0) >= ITEM_SHOW_COUNT;
  const isScrollNow = useRef<boolean>(false);

  const containerStaticStyle = useMemo(
    () => ({
      position: 'relative' as const,
      height: width,
      width: width,
      overflow: 'hidden' as const,
    }),
    [width],
  );

  const containerStyle = useAnimatedStyle(() => {
    if (!scrollY || scrollY.value >= 0) return containerStaticStyle;

    return {
      position: 'relative',
      height: interpolate(
        scrollY.value,
        [-width, 0],
        [width * 2, width],
        'clamp',
      ),
      width: width,
      transform: [
        {
          translateY: interpolate(
            scrollY.value,
            [-width, 0],
            [-width, 0],
            'clamp',
          ),
        },
      ],
      overflow: 'hidden',
    };
  });
  const [isVisible, setIsVisible] = React.useState(false);

  const carouselList = useMemo(() => {
    const images =
      isNotNil(goodsBasic) && isNotNil(goodsBasic.cover_images)
        ? goodsBasic?.cover_images
        : (goodsBasicSkeleton?.cover_images ?? []);

    const imageList = images.map(item => ({
      type: 'image' as const,
      uri: item,
    }));

    if (!successBasicData || !hasSimilarGoods) {
      return imageList;
    }

    return [...imageList, { type: 'related' as const }];
  }, [
    goodsBasic,
    goodsBasicSkeleton?.cover_images,
    successBasicData,
    hasSimilarGoods,
  ]);

  const lastImage = useMemo(() => {
    if (!goodsBasic) return null;

    if (goodsBasic.cover_images.length > 0) {
      return goodsBasic.cover_images[goodsBasic.cover_images.length - 1];
    }
    return null;
  }, [goodsBasic]);

  const isShowArrow = useMemo(() => isWeb, []);

  const scrollX = useSharedValue(0);

  const onPressImage = useCallback(() => {
    if (isScrollNow.current) return;
    setIsVisible(true);
  }, []);

  // ── 네이티브: ScrollView 스크롤 핸들러 ──
  const scrollHandler = useAnimatedScrollHandler({
    onBeginDrag: () => {
      isScrollNow.current = true;
    },
    onScroll: (event: NativeScrollEvent) => {
      const offsetX = event.contentOffset.x;
      const progress = offsetX / width;
      scrollX.value = progress;
    },
    onMomentumEnd: (event: NativeScrollEvent) => {
      isScrollNow.current = false;
      const newIndex = Math.round(event.contentOffset.x / width);
      if (newIndex !== currentIndexValue.value) {
        currentIndexValue.value = newIndex;
      }
    },
  });

  const handleMomentumScrollEnd = useCallback(
    (e: { nativeEvent: NativeScrollEvent }) => {
      const newIndex = Math.round(e.nativeEvent.contentOffset.x / width);
      setCurrentIndex(newIndex);
    },
    [width],
  );

  // ── 웹: Carousel onProgressChange 핸들러 ──
  const onScrollStart = useCallback(() => {
    isScrollNow.current = true;
  }, []);

  const onScrollEnd = useCallback(() => {
    isScrollNow.current = false;
  }, []);

  const onProgressChange = useCallback(
    (_: number, absoluteProgress: number) => {
      scrollX.value = absoluteProgress;
      const newIndex = Math.round(absoluteProgress);
      if (newIndex !== currentIndexValue.value) {
        currentIndexValue.value = newIndex;
        setCurrentIndex(newIndex);
      }
    },
    [currentIndexValue, scrollX],
  );

  // ── 공통: 인덱스 이동 ──
  const scrollToIndex = useCallback(
    (targetIndex: number) => {
      if (isWeb) {
        // SliderRef는 prev/next만 지원하므로 방향에 따라 호출
        const diff = targetIndex - currentIndex;
        if (diff > 0) {
          sliderRef.current?.next();
        } else if (diff < 0) {
          sliderRef.current?.prev();
        }
      } else {
        reanimatedScrollTo(scrollViewRef, targetIndex * width, 0, true);
      }
    },
    [width, scrollViewRef, currentIndex],
  );

  // ── 공통: renderItem (웹 Carousel용) ──
  const renderItem = useCallback(
    ({ item, index }: { item: CarouselItem; index: number }) => {
      if (item.type === 'image') {
        return (
          <CoverImageItem
            uri={item.uri}
            width={width}
            index={index}
            currentIndexValue={currentIndexValue}
            scrollY={scrollY}
            onPress={onPressImage}
          />
        );
      }

      return (
        <Flex testID={`상품대표이미지-추천상품`}>
          {isNotNil(lastImage) && (
            <RemoteImage
              uri={lastImage}
              width={width}
              height={width}
              blurRadius={10}
            />
          )}
          <BlurBackground />
          <RelatedGoodsView focused={currentIndex === index} />
        </Flex>
      );
    },
    [width, currentIndexValue, scrollY, onPressImage, lastImage, currentIndex],
  );

  // ── 캐러셀 영역 렌더 ──
  const renderCarousel = () => {
    // 웹: reanimated-carousel 사용
    // - scroll-snap 기반 ScrollView는 관성에 따라 2~3장씩 넘어가는 이슈가 있어
    //   한 페이지씩 스냅이 보장되는 Carousel 라이브러리를 사용
    if (isWeb) {
      return (
        <Carousel<CarouselItem>
          sliderRef={sliderRef}
          style={{ backgroundColor: Colors.bg_secondary }}
          data={carouselList}
          renderItem={renderItem}
          loop={false}
          vertical={false}
          width={width}
          height={width}
          onScrollStart={onScrollStart}
          onScrollEnd={onScrollEnd}
          onProgressChange={onProgressChange}
        />
      );
    }

    // 네이티브: Animated.ScrollView + pagingEnabled
    // - 외부 라이브러리 없이 네이티브 pagingEnabled로 한 페이지씩 스냅
    // - UI 스레드에서 useAnimatedScrollHandler로 스크롤 추적
    return (
      <AnimatedScrollView
        ref={scrollViewRef}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        bounces={false}
        onScroll={scrollHandler}
        onMomentumScrollEnd={handleMomentumScrollEnd}
        scrollEventThrottle={16}
        style={{ backgroundColor: Colors.bg_secondary }}
      >
        {carouselList.map((item, index) => {
          if (item.type === 'image') {
            // 현재±RENDER_WINDOW 밖이면 placeholder만 — paging 슬롯(width)은
            // 유지하되 실제 이미지는 마운트하지 않아 메모리를 회수한다.
            const isWithinWindow =
              Math.abs(index - currentIndex) <= RENDER_WINDOW;
            if (!isWithinWindow) {
              return (
                <Flex
                  key={`cover-${index}`}
                  width={width}
                  height={width}
                  backgroundColor={'bg_secondary'}
                />
              );
            }
            return (
              <CoverImageItem
                key={`cover-${index}`}
                uri={item.uri}
                width={width}
                index={index}
                currentIndexValue={currentIndexValue}
                scrollY={scrollY}
                onPress={onPressImage}
              />
            );
          }

          return (
            <Flex
              key={`related-${index}`}
              width={width}
              height={width}
              testID={`상품대표이미지-추천상품`}
            >
              {isNotNil(lastImage) && (
                <RemoteImage
                  uri={lastImage}
                  width={width}
                  height={width}
                  blurRadius={10}
                />
              )}
              <BlurBackground />
              <RelatedGoodsView focused={currentIndex === index} />
            </Flex>
          );
        })}
      </AnimatedScrollView>
    );
  };

  return (
    <Animated.View style={isIOS ? containerStyle : {}}>
      {renderCarousel()}
      {carouselList[currentIndex]?.type !== 'related' && (
        <Flex
          padding={{ horizontal: 16, vertical: 16 }}
          position={'absolute'}
          right={0}
          bottom={0}
          onPress={() => setIsVisible(true)}
        >
          <IconFullscreen fill={'white'} />
        </Flex>
      )}
      {(currentIndex === 0 || !isIOS) && (
        <Flex
          padding={{ horizontal: 16, vertical: 16 }}
          position={'absolute'}
          left={0}
          bottom={0}
        >
          <AiStudioRedirect />
        </Flex>
      )}

      {isShowArrow && currentIndex > 0 && (
        <Flex.Center top={0} bottom={0} left={0} position={'absolute'}>
          <Flex.Center
            width={40}
            height={40}
            backgroundColor={'bg_black_button'}
            backgroundColorOpacity={0.4}
            onPress={() => scrollToIndex(currentIndex - 1)}
          >
            <IconArrowLeft size={24} fill={ColorValues.gray50} />
          </Flex.Center>
        </Flex.Center>
      )}

      {isShowArrow && currentIndex < carouselList.length - 1 && (
        <Flex.Center top={0} bottom={0} right={0} position={'absolute'}>
          <Flex.Center
            width={40}
            height={40}
            backgroundColor={'bg_black_button'}
            backgroundColorOpacity={0.4}
            onPress={() => scrollToIndex(currentIndex + 1)}
          >
            <IconArrowRight size={24} fill={ColorValues.gray50} />
          </Flex.Center>
        </Flex.Center>
      )}

      <Flex.Center position={'absolute'} left={0} right={0} bottom={16}>
        <PaginationDots
          value={currentIndex}
          scrollX={scrollX}
          maxCount={
            (goodsBasic?.cover_images ?? [0, 1]).length +
            (hasSimilarGoods ? 1 : 0)
          }
        />
      </Flex.Center>

      {isNotNil(goodsBasic) && isNotNil(goodsBasic.cover_images) && (
        <DetailedImageViewer
          visible={isVisible}
          images={goodsBasic.cover_images}
          onCloseViewer={() => setIsVisible(false)}
          currentIndex={Math.min(
            currentIndex,
            goodsBasic.cover_images.length - 1,
          )}
        />
      )}
    </Animated.View>
  );
};
